import pLimit from "p-limit";
import { useCallback, useRef, useState, useEffect } from "react";
import { Id, toast, UpdateOptions } from "react-toastify";
import { fetchItem, postItem } from "../../queries/queries-helper.ts";
import ResponseApi from "../../models/interfaces/response-api.ts";
import { sleep } from "../../utils/async-utils.ts";
import { JobAdapter, UnifiedJobStatus } from "../../models/interfaces/dtos/job.ts";

export interface JobStatusMessage {
    pending?: string | ((current: number, total: number) => string);
    success?: string;
    error?: string | ((index: number) => string);
}

const DEFAULT_MESSAGES: Required<JobStatusMessage> = {
    pending: (current, total) => `Processus ${current} sur ${total} en cours...`,
    success: 'Travail terminé.',
    error: 'Une erreur est survenue.'
};

const POLLING_INTERVAL = 2000;
const MAX_RETRIES = 5;
const MAX_CONCURRENCY = 2;

interface UseJobsAsyncOptions<TJob> {
    jobsUrl: string[];
    jobBody?: unknown;
    getStatusUrl: (projectId: string, jobId: string) => string;
    adapter: JobAdapter<TJob>;
    onJobDone?: (
        job: TJob,
        status: TJob,
        jobUrl: string,
        jobId: string,
        toastId: Id
    ) => Promise<void> | void;
    allJobsFinished?: () => void;
    onError?: (index: number) => void;
    jobStatusMessage?: JobStatusMessage;
}

const useJobsAsync = <TJob,>({
    jobsUrl,
    jobBody,
    getStatusUrl,
    adapter,
    onJobDone,
    allJobsFinished,
    onError,
    jobStatusMessage
}: UseJobsAsyncOptions<TJob>) => {

    const [isPolling, setIsPolling] = useState(false);
    const isMounted = useRef(true);

    useEffect(() => () => {
        isMounted.current = false;
    }, []);

    const updateToast = useCallback((toastId: Id, options: UpdateOptions) => {
        if (toastId && isMounted.current) {
            toast.update(toastId, {
                isLoading: false,
                autoClose: 5000,
                closeButton: true,
                ...options
            });
        }
    }, []);

    const handleError = useCallback(
        (index: number, toastId: Id, message?: string) => {
            const errorMessage = message ?? jobStatusMessage?.error ?? DEFAULT_MESSAGES.error;
            const finalMessage =
                typeof errorMessage === "function"
                    ? errorMessage(index)
                    : errorMessage;

            updateToast(toastId, {
                render: finalMessage,
                type: "error",
                autoClose: false
            });

            onError?.(index);
        },
        [jobStatusMessage, onError, updateToast]
    );

    const launchJobs = useCallback(async () => {
        setIsPolling(true);

        const limit = pLimit(MAX_CONCURRENCY);

        try {
            await Promise.allSettled(
                jobsUrl.map((jobUrl, index) =>
                    limit(async () => {
                        if (!isMounted.current) return;

                        const pendingMsg =
                            jobStatusMessage?.pending ?? DEFAULT_MESSAGES.pending;

                        const message =
                            typeof pendingMsg === "function"
                                ? pendingMsg(index + 1, jobsUrl.length)
                                : pendingMsg;

                        const toastId = toast.loading(message);

                        try {
                            // 1. Lancement du job
                            const jobResponse =
                                await postItem<ResponseApi<TJob>>(jobUrl, jobBody);

                            if (!jobResponse?.success) {
                                handleError(
                                    index,
                                    toastId,
                                    jobResponse?.errorMessages?.[0]
                                );

                                return;
                            }

                            const jobEntity = jobResponse.entity;

                            const projetId = adapter.getProjectId(jobEntity);
                            const jobId = adapter.getId(jobEntity);
                            // const fileNameResult =
                            //     adapter.getFileNameResult(jobEntity);
                            let retries = 0;

                            // 2. Polling du job
                            const pollStatus = async (): Promise<boolean> => {
                                try {
                                    const statusDto = await fetchItem<TJob>(
                                        getStatusUrl(projetId, jobId)
                                    );

                                    // La requête HTTP a réussi :
                                    // on remet le compteur d'erreurs consécutives à zéro.
                                    retries = 0;

                                    if (!statusDto) {
                                        handleError(
                                            index,
                                            toastId,
                                            "Job introuvable."
                                        );

                                        return true;
                                    }

                                    const status = adapter.getStatus(statusDto);

                                    if (status === UnifiedJobStatus.Success) {
                                        try {
                                            await onJobDone?.(
                                                jobEntity,
                                                statusDto,
                                                jobUrl,
                                                jobId,
                                                toastId
                                            );
                                        } catch (error) {
                                            console.error(
                                                `Erreur pendant le traitement du job ${jobId}:`,
                                                error
                                            );

                                            handleError(
                                                index,
                                                toastId,
                                                error instanceof Error
                                                    ? error.message
                                                    : undefined
                                            );

                                            return true;
                                        }

                                        updateToast(toastId, {
                                            render:
                                                jobStatusMessage?.success ??
                                                DEFAULT_MESSAGES.success,
                                            type: "success"
                                        });

                                        return true;
                                    }

                                    if (status === UnifiedJobStatus.Error) {
                                        handleError(index, toastId);
                                        return true;
                                    }

                                    await sleep(POLLING_INTERVAL);

                                    return false;
                                } catch (error) {
                                    retries += 1;

                                    console.error(
                                        `Erreur pendant le polling du job ${jobId} ` +
                                        `(${retries}/${MAX_RETRIES}):`,
                                        error
                                    );

                                    if (retries <= MAX_RETRIES) {
                                        await sleep(POLLING_INTERVAL);
                                        return false;
                                    }

                                    handleError(
                                        index,
                                        toastId,
                                        error instanceof Error
                                            ? error.message
                                            : undefined
                                    );

                                    return true;
                                }
                            };

                            let finished = false;

                            while (!finished && isMounted.current) {
                                // eslint-disable-next-line no-await-in-loop
                                finished = await pollStatus();
                            }
                        } catch (error) {
                            // Capture notamment les erreurs provenant de postItem(),
                            // comme ton HTTP 505.
                            console.error(
                                `Erreur lors du lancement du job ${jobUrl}:`,
                                error
                            );

                            handleError(
                                index,
                                toastId,
                                error instanceof Error
                                    ? error.message
                                    : undefined
                            );
                        }
                    })
                )
            );

            if (isMounted.current) {
                allJobsFinished?.();
            }
        } finally {
            if (isMounted.current) {
                setIsPolling(false);
            }
        }
    }, [
        adapter,
        allJobsFinished,
        getStatusUrl,
        handleError,
        jobStatusMessage,
        jobsUrl,
        jobBody,
        onJobDone,
        updateToast
    ]);

    return {
        launchJobs,
        isPolling
    };
};

export default useJobsAsync;
